import styles from "./styles.module.scss"
import { CruiseSearchResultLiked } from "../SystemMessageComponents/CruiseSearchResultLiked"
import { _t } from "@cruisen/i18n"
import { timestampToTime } from "@cruisen/react-shared"
import { NavigatorSystemNotificationData, TripRoomID, UserID } from "@cruisen/contract"
import { TripRoomsChanged } from "../SystemMessageComponents/TripRoomsChanged"
import { TripGuestsChanged } from "../SystemMessageComponents/TripGuestsChanged"
import NavigatorAvatar from "../../atoms/NavigatorAvatar"
import { UserBookedCabin } from "../SystemMessageComponents/UserBookedCabin"
import { TripSummary } from "../SystemMessageComponents/TripSummary"
import { CollaboratorJoinedTrip } from "../SystemMessageComponents/CollaboratorJoinedTrip"
import { CreatedWelcomeMessage } from "../SystemMessageComponents/CreatedWelcomeMessage"
import { JoinedWelcomeMessage } from "../SystemMessageComponents/JoinedWelcomeMessage"
import { useStores } from "../../../models/helpers/useStores"
import UserAvatar from "../../atoms/UserAvatar"
import classNames from "classnames"
import { NavigatorCruiseRecommendation } from "../SystemMessageComponents/NavigatorCruiseRecommendation"
import { UserEngagement } from "../SystemMessageComponents/UserEngagement"
import { UserSetPreferredCruise } from "../SystemMessageComponents/UserSetPreferredCruise"
import { SavedCruiseCabins } from "../SystemMessageComponents/SavedCruiseCabins"
import Linkify from "react-linkify"
import { PaymentRequestMessage } from "../SystemMessageComponents/PaymentRequestMessage"
import { handleNotificationAction } from "../../../services/notifications-navigation.ts"
import { UserApprovedPaymentRequestMessage } from "../SystemMessageComponents/UserApprovedPaymentRequestMessage/index.tsx"

export type CruiseCard = {
  userFirstName: string
  userLastNameInitial: string
  image: string
  cruiseSailingName: string
  cruiseSailingLineName: string
  location: string
  date: string
}

type SystemMessageAuthorDetails = {
  firstName: string
  lastName?: string
  avatar?: string
}

export const SystemMessage = ({
  message,
  messageData,
  showAvatarAndName,
  currentUserId,
  tripOwnerId,
  timeStamp,
}: {
  message: string
  messageData: NavigatorSystemNotificationData
  showAvatarAndName: boolean
  currentUserRoomId: TripRoomID
  currentUserRoomName: string
  currentUserId: UserID
  tripOwnerId: UserID
  timeStamp: number
}) => {
  const rootStore = useStores()
  const { activeTrip: store } = rootStore

  const isSomeRoomAvailableForBooking = store.rooms.some(
    (room) => room.bookingDetails === null && room.guests.length !== 0,
  )

  let content: JSX.Element | null = null
  let authorDetails: SystemMessageAuthorDetails

  /**
   * Control variables
   *
   * showAvatarAndName - defaulted based on who sent last message and when last message was sent
   * showMessageHeader - force show the avatar and timestamp above a message
   * isHiddenMessage - force hide a message for some users
   * isGenericNotification - force hide avatar and timestamp (offsets showAvatarAndName)
   * isMessageFromCurrentUser - is message a system message initiated by the logged in user
   */
  let showMessageHeader = false
  let isHiddenMessage = false
  let isGenericNotification = false
  let isMessageFromCurrentUser = false

  if (!messageData.data && messageData.type !== "navigator") return null

  switch (messageData.type) {
    case "welcome":
      content = (
        <CreatedWelcomeMessage
          userFirstName={messageData.data.notification.data.userFirstName}
          userId={messageData.data.notification.data.userId}
          currentUserId={currentUserId}
        />
      )
      showMessageHeader = true
      isHiddenMessage = messageData.data.notification.data.userId !== currentUserId
      break
    case "tripUpdated":
      content = <TripSummary data={messageData.data.notification.data} created={false} />
      isGenericNotification = true
      break
    case "tripCreated":
      content = <TripSummary data={messageData.data.notification.data} created={true} />
      isHiddenMessage = messageData.data.notification.data.userId === currentUserId
      isGenericNotification = true
      break
    // case "cruiseSuggestion":
    //   content = <CruiseSuggestions cruiseData={data.data} />
    //   break
    case "userLikedTripSearchResult":
      content = (
        <CruiseSearchResultLiked
          data={messageData.data.notification.data}
          currentUserId={currentUserId}
        />
      )
      isGenericNotification = true
      break
    case "savedCruiseCabins":
      content = (
        <SavedCruiseCabins
          data={messageData.data.notification.data}
          messageId={messageData.data.notification.id}
        />
      )
      isGenericNotification = true
      break
    case "userSetPreferredCruise":
      content = (
        <UserSetPreferredCruise
          data={messageData.data.notification.data}
          currentUserId={currentUserId}
        />
      )
      isGenericNotification = true
      break
    case "navigatorCruiseRecommendation":
      content = <NavigatorCruiseRecommendation data={messageData.data.notification.data} />
      break
    case "tripRoomCreated":
      content = (
        <TripRoomsChanged
          data={messageData.data.notification.data}
          created={true}
          iconName={"plus-circle"}
        />
      )
      isGenericNotification = true
      break
    case "tripRoomRenamed":
      content = (
        <TripRoomsChanged
          data={messageData.data.notification.data}
          created={false}
          iconName={"edit"}
        />
      )
      isGenericNotification = true
      break
    case "collaboratorJoinedTrip":
      if (
        tripOwnerId !== currentUserId &&
        currentUserId === messageData.data.notification.data.userId
      ) {
        showMessageHeader = true
        content = (
          <JoinedWelcomeMessage
            data={messageData.data.notification.data}
            showAvatarAndName={showMessageHeader || showAvatarAndName}
          />
        )
      } else {
        content = <CollaboratorJoinedTrip data={messageData.data.notification.data} />
        isGenericNotification = true
      }
      break
    case "guestJoinedTripRoom":
      content = (
        <TripGuestsChanged
          data={messageData.data.notification.data}
          joined={true}
          iconName={"people"}
        />
      )
      isGenericNotification = true
      break
    case "guestLeavedTripRoom":
      content = (
        <TripGuestsChanged
          data={messageData.data.notification.data}
          joined={false}
          iconName={"person-delete"}
        />
      )
      isGenericNotification = true
      break
    case "userBookedCabin":
      content = (
        <UserBookedCabin
          data={messageData.data.notification.data}
          isSomeRoomAvailableForBooking={isSomeRoomAvailableForBooking}
          currentUserId={currentUserId}
        />
      )
      isGenericNotification = true
      break
    case "userEngagement":
      if (messageData.data.notification.data?.userFirstName) {
        showMessageHeader = true
        authorDetails = {
          firstName: messageData.data.notification.data.userFirstName,
          lastName: messageData.data.notification.data.userLastName,
          avatar: messageData.data.notification.data.userAvatar,
        }
      }
      content = <UserEngagement data={messageData.data.notification.data} message={message} />
      break
    case "paymentRequest":
      if (messageData.data.notification.data?.userFirstName) {
        showMessageHeader = true
        authorDetails = {
          firstName: messageData.data.notification.data.userFirstName,
          lastName: messageData.data.notification.data.userLastName,
          avatar: messageData.data.notification.data.userAvatar,
        }
      }
      if (messageData.data.notification.data?.userId === currentUserId) {
        isMessageFromCurrentUser = true
      }
      content = (
        <PaymentRequestMessage
          data={messageData.data.notification.data}
          openPaymentRequestDetails={() =>
            handleNotificationAction(
              { id: "openPaymentRequest" },
              messageData.data.notification.data,
            )
          }
        />
      )
      break
    case "userApprovedPaymentRequest":
      content = (
        <UserApprovedPaymentRequestMessage
          openPaymentRequestDetails={() => {
            handleNotificationAction(
              { id: "openPaymentRequest" },
              messageData.data.notification.data,
            )
          }}
          data={messageData.data.notification.data}
        />
      )
      isGenericNotification = true
      break
    default:
      content = null
      showMessageHeader = true
  }

  if (isHiddenMessage) return null

  return (
    <div
      className={styles.column}
      //testID="navigator-message"
    >
      {(showMessageHeader || showAvatarAndName) && !isGenericNotification && !isHiddenMessage && (
        <div className={styles.messageHeader}>
          {authorDetails ? (
            isMessageFromCurrentUser ? (
              <>
                <div className={styles.userFillerRow}></div>
                <p className={styles.timeStamp}>{timestampToTime(timeStamp)}</p>
                <UserAvatar
                  nameInitals={`${authorDetails.firstName[0]}${authorDetails.lastName?.[0]}`}
                  avatar={authorDetails.avatar}
                  size={"sm"}
                  isPrimary={true}
                />
              </>
            ) : (
              <>
                <UserAvatar
                  nameInitals={`${authorDetails.firstName[0]}${authorDetails.lastName?.[0]}`}
                  avatar={authorDetails.avatar}
                  size={"sm"}
                  isPrimary={false}
                />
                <div>
                  <div className={styles.userRow}>
                    <p className={styles.nameText}>
                      {authorDetails.firstName} {authorDetails.lastName?.[0] || ""}.
                    </p>
                    <p className={styles.timeStamp}>{timestampToTime(timeStamp)}</p>
                  </div>
                  <p className={styles.fromCruisen}>{_t("navigator.fromCruisen")}</p>
                </div>
              </>
            )
          ) : (
            <>
              <NavigatorAvatar />
              <div className={styles.navigatorRow}>
                <p className={styles.nameText}>{_t("navigator.name")} ·</p>
                <p className={styles.timeStamp}>{timestampToTime(timeStamp)}</p>
              </div>
            </>
          )}
        </div>
      )}
      <div
        className={classNames(
          styles.messageContainer,
          isHiddenMessage
            ? styles.hiddenMessageMargin
            : isGenericNotification
              ? styles.genericNotificationMargin
              : styles.nonHiddenMessageMargin,
        )}
      >
        {!content ? (
          <div
            className={classNames(
              styles.message,
              showMessageHeader ? styles.negativeMarginTop : "",
            )}
            //testID="navigator-ai-message"
          >
            <p>
              <Linkify
                componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="blank" href={decoratedHref} key={key}>
                    {decoratedText}
                  </a>
                )}
              >
                {message}
              </Linkify>
            </p>
            {messageData.data?.notification?.actions?.length > 0 ? (
              <div className={styles.actionsContainer}>
                {messageData.data?.notification?.actions?.map((action, index) => (
                  <button
                    key={index}
                    className={styles.actionButton}
                    onClick={() =>
                      handleNotificationAction(action, messageData.data.notification.data)
                    }
                  >
                    {action.buttonText ?? _t("common.view")}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          content
        )}
      </div>
    </div>
  )
}
